import React, { useRef, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  CreateOutlined as DrawIcon,
  TextFieldsOutlined as TypeIcon,
  ClearOutlined as ClearIcon,
  CheckCircleOutline as CheckIcon
} from '@mui/icons-material';
import SignatureCanvas from 'react-signature-canvas';
import { brand } from '../../theme/colors';
import DialogCloseButton from '../common/DialogCloseButton';

/**
 * Digital Signature Capture Component
 * Supports both drawn and typed signatures with legal consent
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Close callback
 * @param {Function} props.onSignatureComplete - Callback with signature data
 * @param {string} props.documentType - 'bid' or 'milestone_report'
 * @param {number} props.documentId - Document ID
 * @param {Object} props.documentData - Full document data for hashing
 * @param {string} props.signerName - Name of signer
 * @param {string} props.signerEmail - Email of signer
 * @param {string} props.consentText - Consent agreement text
 */
const DigitalSignatureCapture = ({
  open,
  onClose,
  onSignatureComplete,
  documentType,
  documentId,
  documentData,
  signerName,
  signerEmail,
  consentText
}) => {
  const signatureCanvasRef = useRef(null);
  const [signatureMode, setSignatureMode] = useState('draw'); // 'draw' or 'type'
  const [typedSignature, setTypedSignature] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [geolocation, setGeolocation] = useState(null);

  // Get geolocation when component mounts
  useEffect(() => {
    if (open && 'geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGeolocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          console.log('Geolocation not available:', error);
          // Geolocation is optional, so we don't show error
        }
      );
    }
  }, [open]);

  // Handle mode change
  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSignatureMode(newMode);
      setError('');
    }
  };

  // Clear signature
  const handleClear = () => {
    if (signatureMode === 'draw' && signatureCanvasRef.current) {
      signatureCanvasRef.current.clear();
    } else {
      setTypedSignature('');
    }
    setError('');
  };

  // Validate signature
  const validateSignature = () => {
    // Check consent
    if (!consentAccepted) {
      setError('You must accept the consent agreement to continue');
      return false;
    }

    // Check signature exists
    if (signatureMode === 'draw') {
      if (!signatureCanvasRef.current || signatureCanvasRef.current.isEmpty()) {
        setError('Please draw your signature');
        return false;
      }
    } else {
      if (!typedSignature.trim()) {
        setError('Please type your full name');
        return false;
      }
    }

    return true;
  };

  // Handle signature submission
  const handleSubmit = async () => {
    setError('');

    if (!validateSignature()) {
      return;
    }

    setLoading(true);

    try {
      let signatureData;

      if (signatureMode === 'draw') {
        // Get signature as base64 PNG
        signatureData = signatureCanvasRef.current.toDataURL('image/png');
      } else {
        // For typed signature, we'll just store the text
        signatureData = typedSignature;
      }

      const signaturePayload = {
        documentType,
        documentId,
        signatureType: signatureMode === 'draw' ? 'drawn' : 'typed',
        signatureData,
        fullName: signerName,
        email: signerEmail,
        geolocation,
        documentData,
        consentText
      };

      await onSignatureComplete(signaturePayload);
    } catch (err) {
      setError(err.message || 'Failed to save signature');
      setLoading(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (!loading) {
      handleClear();
      setConsentAccepted(false);
      setError('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: 'linear-gradient(to bottom, #ffffff 0%, #f9fafb 100%)'
        }
      }}
    >
      <DialogCloseButton onClick={handleClose} />
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckIcon sx={{ color: brand.primary }} />
          <Typography variant="h6" component="span">
            Digital Signature Required
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please review and sign this {documentType === 'bid' ? 'bid' : 'milestone report'} to proceed
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Signature Mode Toggle */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Choose Signature Method:
          </Typography>
          <ToggleButtonGroup
            value={signatureMode}
            exclusive
            onChange={handleModeChange}
            aria-label="signature mode"
            fullWidth
          >
            <ToggleButton value="draw" aria-label="draw signature">
              <DrawIcon sx={{ mr: 1 }} />
              Draw Signature
            </ToggleButton>
            <ToggleButton value="type" aria-label="type signature">
              <TypeIcon sx={{ mr: 1 }} />
              Type Name
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Signature Canvas (Draw Mode) */}
        {signatureMode === 'draw' && (
          <Paper
            elevation={0}
            sx={{
              border: '2px solid #e0e0e0',
              borderRadius: 1,
              mb: 2,
              background: '#ffffff',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                Sign above with your mouse or touchscreen
              </Typography>
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={handleClear}
                sx={{ minWidth: 'auto' }}
              >
                Clear
              </Button>
            </Box>
            <SignatureCanvas
              ref={signatureCanvasRef}
              canvasProps={{
                width: 500,
                height: 200,
                className: 'signature-canvas',
                style: { width: '100%', height: '200px' }
              }}
              backgroundColor="#ffffff"
              penColor="#000000"
            />
          </Paper>
        )}

        {/* Typed Signature (Type Mode) */}
        {signatureMode === 'type' && (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Type Your Full Name"
              value={typedSignature}
              onChange={(e) => setTypedSignature(e.target.value)}
              placeholder="John Doe"
              variant="outlined"
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '24px',
                  fontFamily: 'cursive',
                  fontStyle: 'italic'
                }
              }}
            />
            {typedSignature && (
              <Paper
                elevation={0}
                sx={{
                  mt: 2,
                  p: 3,
                  border: '2px solid #e0e0e0',
                  borderRadius: 1,
                  background: '#ffffff',
                  textAlign: 'center'
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: 'cursive',
                    fontStyle: 'italic',
                    color: '#000000'
                  }}
                >
                  {typedSignature}
                </Typography>
              </Paper>
            )}
          </Box>
        )}

        {/* Signer Information */}
        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Signing As:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Name:</strong> {signerName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Email:</strong> {signerEmail}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Date:</strong> {new Date().toLocaleString()}
          </Typography>
        </Paper>

        {/* Consent Agreement */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            bgcolor: '#fffbf0',
            border: '1px solid #ffd666',
            borderRadius: 1,
            maxHeight: 200,
            overflow: 'auto'
          }}
        >
          <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
            {consentText}
          </Typography>
        </Paper>

        {/* Consent Checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={consentAccepted}
              onChange={(e) => setConsentAccepted(e.target.checked)}
              sx={{
                color: brand.primary,
                '&.Mui-checked': {
                  color: brand.primary
                }
              }}
            />
          }
          label={
            <Typography variant="body2">
              <strong>I agree</strong> to the electronic signature consent and confirm that I have reviewed the complete document. My signature creates a binding legal agreement.
            </Typography>
          }
        />

        {/* Legal Disclaimer */}
        <Alert severity="info" sx={{ mt: 2 }} icon={false}>
          <Typography variant="caption">
            Your signature is protected with industry-standard encryption and timestamped for legal validity under the Electronic Transactions Act [Chapter 22:05] of Zimbabwe, the ESIGN Act, and UETA.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            background: 'linear-gradient(45deg, #10B981 30%, #34D399 90%)',
            '&:hover': {
              background: 'linear-gradient(45deg, #059669 30%, #10B981 90%)'
            }
          }}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
        >
          {loading ? 'Signing...' : 'I Agree and Sign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DigitalSignatureCapture;
